import React, { useState, useRef, useEffect } from "react";
import { MoreHorizontal, Eye, Download, Pencil } from "lucide-react";
import { Design } from "../useDesignManagement";
import { DesignMenuAction, getDesignMenuActions } from "../utils/designMenuActions";

interface DesignDropdownMenuProps {
  design: Design;
  onPreview: (design: Design) => void;
  onDownload?: (design: Design) => void;
  onEdit?: (design: Design) => void;
}

interface MenuItem {
  icon: React.FC<any>;
  label: string;
  action: () => void;
  primary?: boolean;
  destructive?: boolean;
}

const DesignDropdownMenu: React.FC<DesignDropdownMenuProps> = ({ design, onPreview, onDownload, onEdit }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleMenuAction = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  // Edit is offered for every design; builderParams decides how it opens (PAD-32396).
  const itemsByAction: Record<DesignMenuAction, MenuItem> = {
    preview: { icon: Eye, label: "Preview", action: () => onPreview(design), primary: true },
    edit: { icon: Pencil, label: "Edit", action: () => onEdit?.(design), primary: false },
    download: { icon: Download, label: "Download", action: () => onDownload?.(design), primary: false },
  };
  const menuItems: MenuItem[] = getDesignMenuActions({ onEdit, onDownload }).map((key) => itemsByAction[key]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        data-cy="designDropdownMenuButton"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="w-8 h-8 flex items-center justify-center text-blue-light hover:bg-light-blue hover:scale-110 transform transition-all duration-200 ease-in-out rounded-full"
        aria-label="Design options"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 overflow-hidden">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                data-cy={`designDropdownMenuAction-${item.label.toLowerCase()}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuAction(item.action);
                }}
                className={`
                  w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-50 transition-colors duration-150
                  ${item.destructive ? "text-red-600 hover:bg-red-50" : "text-gray-700"}
                  ${item.primary ? "font-medium" : "font-normal"}
                `}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DesignDropdownMenu;
